import { useEffect, useState } from 'react'
import { api } from '../../api/client'

type History = Awaited<ReturnType<typeof api.formSessions>>
type Evidence = Awaited<ReturnType<typeof api.formSession>>
const label = (value: string) => value.replace(/_/g, ' ')

function SessionEvidence({ companyId, jobId }: {companyId: string; jobId: string}) {
  const [data, setData] = useState<Evidence | null>(null)
  const [after, setAfter] = useState(0)
  const [error, setError] = useState('')
  useEffect(() => {
    let live = true
    setData(null)
    const refresh = () => api.formSession(companyId, jobId, after).then(value => {
      if (live) { setData(value); setError('') }
    }).catch(e => { if (live) setError(e.message) })
    void refresh()
    const timer = window.setInterval(refresh, 5000)
    return () => { live = false; window.clearInterval(timer) }
  }, [companyId, jobId, after])
  return <>
    {error && <p className="notice crit" role="alert">{error}</p>}
    {!data ? <p className="muted">Loading session evidence…</p> : <>
      <p className="muted">Contact page: {data.request.target}</p>
      {data.usage?.status === 'complete' ? <p className="muted">Provider usage: {data.usage.browser_seconds?.toLocaleString()} browser seconds · {data.usage.proxy_bytes?.toLocaleString()} proxy bytes. Dollar charges remain subject to billing reconciliation.</p> : <p className="muted">Provider usage has not been collected yet.</p>}
      <details><summary>Prepared form content</summary><pre className="compiled-prompt">{JSON.stringify(data.request.variables, null, 2)}</pre></details>
      <details><summary>Full instructions and payload</summary><pre className="compiled-prompt">{JSON.stringify(data.request, null, 2)}</pre></details>
      <ol className="form-steps">{data.steps.map(step => <li key={step.sequence}>
        <details><summary>{step.sequence}. {label(step.operation || step.event)} · {label(step.event)} · {new Date(step.created_at).toLocaleTimeString()}</summary>
          {step.error && <p className="notice warn">{step.error}</p>}
          {step.result != null && <pre className="compiled-prompt">{typeof step.result === 'string' ? step.result : JSON.stringify(step.result, null, 2)}</pre>}
          {step.confirmation != null && <pre className="compiled-prompt">{JSON.stringify(step.confirmation, null, 2)}</pre>}
          {step.has_screenshot && <img className="form-screenshot" loading="lazy" alt={`Contact form after ${label(step.operation || step.event)}`} src={`/api/dashboard/companies/${encodeURIComponent(companyId)}/form-sessions/${encodeURIComponent(jobId)}/screenshots/${step.sequence}`} />}
        </details>
      </li>)}</ol>
      <div className="action-group">
        {after > 0 && <button className="btn secondary small" onClick={() => setAfter(0)}>First steps</button>}
        {data.next_after !== null && <button className="btn secondary small" onClick={() => setAfter(data.next_after!)}>Later steps</button>}
      </div>
    </>}
  </>
}

export default function FormSessions({ companyId }: {companyId: string}) {
  const [history, setHistory] = useState<History | null>(null)
  const [before, setBefore] = useState<number>()
  const [opened, setOpened] = useState<Record<string, boolean>>({})
  const [error, setError] = useState('')
  useEffect(() => {
    let live = true
    setHistory(null)
    const refresh = () => api.formSessions(companyId, before).then(value => {
      if (live) { setHistory(value); setError('') }
    }).catch(e => { if (live) setError(e.message) })
    void refresh()
    const timer = before === undefined ? window.setInterval(refresh, 5000) : undefined
    return () => { live = false; if (timer !== undefined) window.clearInterval(timer) }
  }, [companyId, before])
  return <section className="panel form-sessions">
    <h2>Contact form sessions</h2>
    {error && <p className="notice crit" role="alert">{error}</p>}
    {!history ? <p className="muted">Loading form history…</p> : !history.rows.length ? <p className="muted">No contact form sessions yet.</p> :
      <div className="workflow-list">{history.rows.map(session => <article className="workflow-card" key={session.job_id}>
        <div className="workflow-meta"><span className={`pill ${session.status === 'submitted' ? 'ok' : ['blocked', 'uncertain'].includes(session.status) ? 'warn' : ''}`}>{label(session.status)}</span><time>{new Date(session.created_at).toLocaleString()}</time></div>
        {session.error && <p className="notice warn">{session.error}</p>}
        {session.confirmation?.text && <p>{session.confirmation.text}</p>}
        {!!session.captcha?.detected && <p className="muted">CAPTCHA checks: {session.captcha.cleared} cleared, {session.captcha.unsolved} unresolved.</p>}
        {session.session_url && <p><a href={session.session_url} target="_blank" rel="noopener noreferrer">Open Browserbase session and recording ↗</a></p>}
        <details className="form-evidence" onToggle={event => { const open = event.currentTarget.open; setOpened(previous => ({...previous, [session.job_id]: open})) }}>
          <summary>Inspect content and execution steps</summary>
          {opened[session.job_id] && <SessionEvidence companyId={companyId} jobId={session.job_id} />}
        </details>
      </article>)}</div>}
    <div className="action-group">
      {before !== undefined && <button className="btn secondary small" onClick={() => setBefore(undefined)}>Latest sessions</button>}
      {history?.next_cursor != null && <button className="btn secondary small" onClick={() => setBefore(history.next_cursor!)}>Older sessions</button>}
    </div>
  </section>
}
